import { Column } from 'primereact/column'
import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import DataTable from '../../../components/ui/DataTable'
import ExpandableText from '../../../components/ui/ExpandableText'
import ActionStatusTag from '../../actions/components/ActionStatusTag'
import ResponsiblesLines from './ResponsiblesLines'
import AttachmentCell from './AttachmentCell'
import TaskOriginMark from './TaskOriginMark'
import EmptyState from '../../../components/ui/EmptyState'
import type { TableSort } from '../../../lib/useTableSort'
import { lateDaysText, taskDurationText, type MeetingManagementTask } from '../types'
import { useCurrentUser } from '../../../auth/useCurrentUser'

/**
 * Açık satırı tutan **abonelik deposu**. Context yalnız bu depoyu taşır ve depo nesnesi **hiç
 * değişmez**; dolayısıyla context değeri de değişmez.
 *
 * <p><b>Neden context gerekiyor (21.09.2026'da ölçüldü):</b> PrimeReact'in tablo gövdesi memoize
 * edilmiştir ve yalnız `value`/`selection` gibi izlediği prop'lar değişince yeniden çizer. Durum
 * tabloda tutulup `body={(row) => <ExpandableText expanded={...} />}` ile geçilince `onToggle`
 * çalışıyor, `setState` çalışıyor, tablo yeniden render oluyor — ama **`body` fonksiyonu yeniden
 * çağrılmıyor**, dolayısıyla hücre eski `expanded={false}` hâliyle ekranda kalıyordu. React context
 * memo sınırlarını **geçer**.
 *
 * <p><b>Neden düz bir context değeri DEĞİL (MR !228, GitLab Duo bulgusu — ölçüldü):</b> context
 * değeri her açma/kapamada değişseydi, o context'i tüketen <b>her</b> açıklama hücresi yeniden
 * render olurdu; yalnız durumu değişen iki satır değil. Ölçüldü: varsayılan sayfa boyutunda tek
 * tıklama <b>20</b> hücreyi yeniden render ediyordu ve tablo sayfa boyutu <b>200</b>'e kadar
 * çıkabiliyor.
 *
 * <p><b>`React.memo` bu sorunu ÇÖZMEZ</b> — Duo'nun önerisi buydu ama memo yalnız <i>prop</i>
 * kaynaklı render'ı durdurur; `useContext` çağıran bir bileşen, context değeri değiştiğinde memo'lu
 * olsa da yeniden render olur. Çözüm abonelik: depo sabit kalır, her hücre
 * {@link useSyncExternalStore} ile <b>kendi</b> boolean'ını seçer ve React anlık görüntü
 * değişmediğinde render'ı atlar. Böylece bir tıklamada yalnız açılan ve kapanan satır çizilir.
 */
interface ExpandStore {
  subscribe: (listener: () => void) => () => void
  isExpanded: (id: number) => boolean
  toggle: (id: number) => void
}

function createExpandStore(): ExpandStore {
  let expandedId: number | null = null
  const listeners = new Set<() => void>()
  return {
    subscribe(listener) {
      listeners.add(listener)
      // Gövde blok: `listeners.delete(...)` boolean döner, temizleyici ise `void` olmalı.
      return () => {
        listeners.delete(listener)
      }
    },
    isExpanded: (id) => expandedId === id,
    toggle(id) {
      // Açık olana basmak kapatır; başkasına basmak öncekini kapatıp yenisini açar.
      expandedId = expandedId === id ? null : id
      listeners.forEach((listener) => listener())
    },
  }
}

const ExpandedRowContext = createContext<ExpandStore>(createExpandStore())

/**
 * Görev Açıklaması hücresi. Ayrı bir bileşen olması **şart**: context'i tüketen bir React bileşeni
 * olmalı ki PrimeReact'in memoize gövdesine rağmen güncellensin (bkz. {@link ExpandedRowContext}).
 */
function DescriptionCell({ row }: Readonly<{ row: MeetingManagementTask }>) {
  const store = useContext(ExpandedRowContext)
  // Anlık görüntü bir BOOLEAN'dır: React `Object.is` ile karşılaştırıp değişmediyse render'ı atlar,
  // dolayısıyla durumu değişmeyen satırlar hiç çizilmez.
  const expanded = useSyncExternalStore(
    store.subscribe,
    useCallback(() => store.isExpanded(row.id), [store, row.id]),
  )

  // Veri yoksa BOŞ hücre (kullanıcı kararı 2026-09-16): çizgi, olmayan bir değeri varmış gibi
  // gösteriyordu — bu yüzden kırpma kutusu da yalnız dolu hücrede çizilir.
  if (!row.description) return null

  return (
    <ExpandableText
      text={row.description}
      lines={4}
      expanded={expanded}
      onToggle={() => store.toggle(row.id)}
      // Erişilebilir ad görev başlığıyla kurulur: aynı listede onlarca "Devamını gör" düğmesi var ve
      // ekran okuyucu/sesli kontrolde ad, düğmenin tek tanımlayıcısıdır (`lib/deleteLabel.ts`).
      itemName={row.name}
    />
  )
}

interface Props {
  rows: MeetingManagementTask[]
  loading: boolean
  totalRecords: number
  first: number
  rows_: number
  onPage: (first: number, rows: number) => void
  sorting: TableSort
  selection: MeetingManagementTask[]
  onSelectionChange: (next: MeetingManagementTask[]) => void
  /**
   * `false` → seçim kolonu hiç çizilmez (Kısıtlı Yetkili asistan, 126 FR-019). Seçim yalnız Kapat /
   * Yeniden Aç için vardır; o işlemler sunulmayan kipte kutu, kullanıcıya olmayan bir eylemi vaat
   * ederdi.
   */
  selectable: boolean
  /** Ataca tıklanınca çağrılır — dosya pop-up'ını sayfa açar (tablo kendi pop-up'ını taşımaz). */
  onOpenFiles: (row: MeetingManagementTask) => void
  /** Satıra (ana ya da alt görev) tıklanınca çağrılır — detay pop-up'ını sayfa açar. */
  onOpenDetail: (row: MeetingManagementTask) => void
  /**
   * Boş liste metni. Verilmezse Toplantı Yönetimi'nin "süzgeçlere uyan görev yok" metni kullanılır;
   * süzgeçsiz onay kuyruğu (131) kendi metnini verir — orada boş liste bir süzgeç sonucu değildir.
   */
  emptyText?: { title: string; description: string }
}

/**
 * Toplantı Yönetimi tablosu (105) — dokuz kolon: **Seçim · Görev Başlığı · Görev Açıklaması ·
 * Sorumlular · Onaya Gönderme Notu · Görev Süresi · Gecikme Gün Sayısı · Durum · Ek**.
 *
 * <p><b>"Ek" kolonu EN SONDADIR</b> (kullanıcı isteği 30.09.2026). Ek göstergesi <b>yalnız ATAÇtır,
 * sayı değil</b> (kullanıcı kuralı aynı gün): ataç varsa dosya vardır, yoksa ataç yerine hiçbir şey
 * çizilmez. Ölçüt sunucudan gelen {@code hasAttachment}'tır; istemci dosya saymaz. Ataçın
 * <b>solunda</b> ana görevin kaynağı durur — "D" Doğrudan / "T" Toplantı Bağımlı
 * ({@code TaskOriginMark}, kullanıcı isteği 02.11.2026), yani ana görevin hücresi dosya yokken de
 * boş değildir; alt görev satırında işaret yoktur. Başlık görsel olarak gizlenir ({@code ui-sr-only})
 * — dar bir kolonda (önce %4, 02.11.2026'dan beri %6) başlık metni satırı sararak tabloyu
 * yükseltirdi; ekran okuyucu kolonu yine adıyla duyurur.
 *
 * <p><b>Çoklu seçim uygulamada ilk kez burada kullanılıyor.</b> Yeni bir sarmalayıcı <b>yazılmadı</b>:
 * paylaşılan {@code ui/DataTable} zaten tüm PrimeReact prop'larını geçiriyor, yani eksik olan bir
 * bileşen değil yalnız bir prop kullanımıydı (Reuse Mandate: "yoksa paylaşılan katmana ekle" —
 * burada eksik bir şey yok).
 *
 * <p><b>Seçilebilirlik sunucudan gelir.</b> Satır, {@code closable} ya da {@code reopenable}
 * taşıyorsa seçilebilir; istemci "durum 331 mi, kullanıcı Özel Kalem mi" hesabını <b>yeniden
 * yapmaz</b>. 066'da istemci koşulu ile uç koşulu ayrışmış, buton aktif görünürken uç hata dönmüştü.
 *
 * <p><b>Soft kırmızı satır</b> yalnız {@code delayedUpdated} bayrağına bakar — istemcide
 * {@code deadline < bugün} hesabı yapılMAZ, aksi halde aynı görev bu ekranda "geciken", Görev
 * Detay'da "gecikmemiş" görünebilirdi.
 */
export default function MeetingManagementTable({
  rows,
  loading,
  totalRecords,
  first,
  rows_,
  onPage,
  sorting,
  selection,
  onSelectionChange,
  selectable,
  onOpenFiles,
  onOpenDetail,
  emptyText,
}: Readonly<Props>) {
  const { t } = useTranslation()
  /** 129 — IT Admin maskesinde başlık/açıklama `***` gelir; sıralama oku çizilmez. */
  const contentMasked = useCurrentUser().data?.actionContentMasked === true

  /**
   * Açıklaması genişletilmiş satır — **aynı anda yalnız bir tane** (kullanıcı isteği 21.09.2026).
   *
   * <p>Depo `useRef` ile bir kez kurulur ve kimliği hiç değişmez; böylece context değeri de sabit
   * kalır ve hücreler yalnız kendi durumları değiştiğinde çizilir (bkz. {@link ExpandedRowContext}).
   */
  const storeRef = useRef<ExpandStore | null>(null)
  storeRef.current ??= createExpandStore()
  const store = storeRef.current

  const rowClass = (row: MeetingManagementTask) =>
    [
      'mtg-row-clickable',
      row.delayedUpdated ? 'mm-row-overdue' : '',
      row.subTask ? 'mm-row-subtask' : '',
    ]
      .filter(Boolean)
      .join(' ')

  return (
    // Sağlayıcı DataTable'ı SARAR: PrimeReact'in memoize gövdesi yeniden çizilmese bile context'i
    // tüketen hücreler (DescriptionCell) güncellenir.
    <ExpandedRowContext.Provider value={store}>
      <DataTable
        value={rows}
        dataKey="id"
        lazy
        loading={loading}
        paginator
        first={first}
        rows={rows_}
        totalRecords={totalRecords}
        onPage={(e) => onPage(e.first ?? 0, e.rows ?? rows_)}
        sortField={sorting.sortField}
        sortOrder={sorting.sortOrder}
        onSort={sorting.onSort}
        selection={selection}
        onSelectionChange={(e) => onSelectionChange(e.value as MeetingManagementTask[])}
        selectionMode="checkbox"
        // Alt görev satırlarında ve işlem hakkı olmayan satırlarda kutu PASİF çizilir; kullanıcı
        // geçersiz bir seçim kuramaz, dolayısıyla "neden çalışmadı?" sorusu hiç doğmaz.
        isDataSelectable={(e) => {
          const row = e.data as MeetingManagementTask
          return selectable && !row.subTask && (row.closable || row.reopenable)
        }}
        className="mm-table"
        // Satır seçim kutusunun erişilebilir adı GÖREV ADIYLA kurulur. Varsayılanı "Row Selected 7"
        // (kayıt kimliği) — sesli kontrol kullanıcısı için hangi satır olduğunu söylemez ve satırlar
        // birbirinden ayrılamaz (`lib/deleteLabel.ts` ile aynı ilke).
        selectionAriaLabel="name"
        rowClassName={rowClass}
        /*
         * Satır tıklaması → görev detay pop-up'ı (kullanıcı isteği 02.10.2026). Seçim kutusu,
         * "Devamını gör" ve ataç düğmelerine tıklama satır tıklaması SAYILMAZ: PrimeReact
         * tıklanabilir hedefleri (input/button/a) eler. `selectionMode="checkbox"` + kutu kolonu
         * birlikteyken satır tıklaması seçimi de değiştirmez (yalnız kutu seçer).
         */
        onRowClick={(e) => onOpenDetail(e.data as MeetingManagementTask)}
        emptyMessage={
          <EmptyState
            icon="pi pi-inbox"
            title={emptyText?.title ?? t('meetingManagement.empty.title')}
            description={emptyText?.description ?? t('meetingManagement.empty.description')}
          />
        }
      >
        {/*
        Kolon genişlikleri YÜZDE ve tablo `table-layout: fixed` (bkz. `.mm-table`); yalnız dar
        kolonlar (seçim, durum) sabit `rem`. Yüzdeler %100'ün altında kalır, tarayıcı kalan alanı
        kolonlara orantılı dağıtır → tablo YATAYDA KAYMAZ (1025–1920px'te ölçüldü, 02.10.2026). Uzun metinler kırpılmaz, alt satıra sarar — kullanıcı
        bildirimi 2026-09-16: "başlık ve açıklama kolonları çok genişti, tablo yatayda scroll oluyordu".
      */}
        {selectable && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />}
        <Column
          field="name"
          header={t('meetingManagement.column.name')}
          sortable={!contentMasked}
          // %18 → %10 (kullanıcı isteği 30.09.2026): başlık kısa, açıklama ve not uzun metin taşır;
          // açılan pay Görev Açıklaması (+4) ile Onaya Gönderme Notu'na (+4) verildi.
          style={{ width: '10%' }}
          // Yazı boyu: `FONT_SIZE_JOB_TITLE` → `--mm-name-font-size`.
          bodyClassName="mm-cell-name"
          body={(row: MeetingManagementTask) => nameBody(row, rows, t, onOpenDetail)}
        />
        <Column
          field="description"
          header={t('meetingManagement.column.description')}
          sortable={!contentMasked}
          // %27 → %23 (20.10.2026): açılan pay Görev Süresi kolonuna verildi; %23 → %21 (02.11.2026):
          // Ek kolonuna (D/T işareti). İkisinde de toplam yüzde değişmedi.
          style={{ width: '21%' }}
          // Yazı boyu: `FONT_SIZE_JOB_DESCRIPTION` → `--mm-desc-font-size`.
          bodyClassName="mm-cell-desc"
          /*
           * Kaydırma kutusunun YERİNİ ALDI (kullanıcı isteği 21.09.2026): dört satırdan uzun
           * açıklamalarda mavi "Devamını gör" düğmesi çıkar, tıklanınca hücre genişler.
           *
           * Aynı zamanda 18.09.2026'da BİLİNÇLE açık bırakılan erişilebilirlik boşluğunu kapatır. Eski
           * çözüm (`max-height` + `overflow: auto`) bağımsız bir kaydırma bölgesi kuruyordu ve
           * kaydırılabilir bir eleman kendiliğinden odaklanabilir DEĞİL (Chrome odaklamaz) — yani
           * klavye-only görsel kullanıcı dört satırdan sonrasını göremiyordu (WCAG 2.1.1; axe
           * `scrollable-region-focusable`). `tabIndex={0}` denenip geri alınmıştı: Sonar
           * `typescript:S6845` RELIABILITY etkisi üretiyor ve `/release` kapısının tek koşulu
           * Reliability Rating = A, yani o hâl merge'i blokluyordu. Gerçek bir `<button>` iki kapıyı da
           * geçer; eski yorumda "kalıcı çözüm" diye planlanan şey buydu.
           */
          body={(row: MeetingManagementTask) => <DescriptionCell row={row} />}
        />
        <Column
          /*
           * `field` "mainResponsible" KALIR, başlık "Sorumlular" olsa da: sıralama sunucu tarafında
           * bu alan adıyla yapılır (sözleşmedeki sıralanabilir alanlar) ve kullanıcı isteği
           * "sıralama yine Ana Sorumluya göre çalışsın" idi. Başlığı değiştirip `field`i de
           * değiştirmek sıralamayı sessizce bozardı.
           */
          field="mainResponsible"
          header={t('meetingManagement.column.responsibles')}
          sortable
          style={{ width: '15%' }}
          body={(row: MeetingManagementTask) => <ResponsiblesLines row={row} />}
        />
        <Column
          field="completionNote"
          header={t('meetingManagement.column.completionNote')}
          sortable
          // %22 → %18 (20.10.2026): Görev Süresi kolonuna pay; bkz. Görev Açıklaması.
          style={{ width: '18%' }}
          // Yazı boyu: `FONT_SIZE_APPROVAL_SUBMISSION_NOTE` → `--mm-note-font-size`.
          bodyClassName="mm-cell-note"
          // Boş not ESKİ kayıtlarda normaldir (29.09.2026'dan önce alan isteğe bağlıydı; geçmişte hiç
          // doldurulmamış) — hata gibi görünmemeli, boş hücre yeter.
          body={(row: MeetingManagementTask) => row.completionNote ?? ''}
        />
        <Column
          field="taskDurationDays"
          header={t('meetingManagement.column.taskDuration')}
          style={{ width: '8%' }}
          // Sıralanamaz: Gecikme Gün Sayısı gibi sunucuda bellekte hesaplanır (termin − oluşturulma günü
          // − 1), sunucu sıralamasında karşılığı yok. Termin yoksa hücre boş kalır.
          body={(row: MeetingManagementTask) => taskDurationText(row.taskDurationDays, t)}
        />
        <Column
          field="lateDays"
          header={t('meetingManagement.column.lateDays')}
          // %10 KALIR: "Gecikme Bulunmamaktadır" tablonun en uzun sabit hücre metnidir; %8'de 1280px'te
          // bir, ~1025px'te üç satır daha sarıyordu (push kapısı review'ü, 21.10.2026 — ölçüldü).
          style={{ width: '10%' }}
          // Sıralanamaz: değer bellekte hesaplanır, sunucu sıralamasında karşılığı yok.
          body={(row: MeetingManagementTask) => lateDaysText(row.lateDays, t)}
        />
        <Column
          field="statusName"
          header={t('meetingManagement.column.status')}
          sortable
          // En uzun rozet ("Aksiyon Bekleniyor") + hücre dolgusu kadar sabit genişlik (02.10.2026).
          style={{ width: '10rem' }}
          // Durum rozeti uygulamanın ORTAK bileşeninden gelir — bu ekran kendi stilini tanımlamaz
          // (Reuse Mandate; kullanıcı bildirimi 2026-09-16: "badge'ler diğer sayfalarla aynı olmalı").
          body={(row: MeetingManagementTask) => (
            <ActionStatusTag statusId={row.statusId} name={row.statusName} />
          )}
        />
        <Column
          header={
            <span className="ui-sr-only">{t('meetingManagement.column.attachment')}</span>
          }
          // %4 → %6 (02.11.2026): ataçın SOLUNA görev kaynağı işareti (D/T) geldi; pay Görev
          // Açıklaması'ndan (%23 → %21) alındı, toplam yüzde değişmedi. Hücre dar kalırsa iki öğe
          // alt alta sarar (`.mm-origin-attach`), tabloyu yatayda taşırmaz.
          style={{ width: '6%' }}
          bodyClassName="mm-cell-origin-attach"
          // Sıralanabilir DEĞİL: kolon iki değerlidir ve sunucunun sıralanabilir alanları arasında
          // yoktur — sıralanabilir görünen ama sessizce düşen bir başlık, kullanıcıya bozuk gelirdi.
          body={(row: MeetingManagementTask) => (
            <div className="mm-origin-attach">
              <TaskOriginMark row={row} />
              <AttachmentCell row={row} onOpenFiles={onOpenFiles} />
            </div>
          )}
        />
      </DataTable>
    </ExpandedRowContext.Provider>
  )
}

/**
 * Görev başlığı — alt görev satırı, bağlı olduğu ana görevin altında olduğu <b>görsel olarak</b>
 * anlaşılsın diye girintili ve işaretli çizilir (FR-015).
 */
/**
 * Görev başlığı hücresi. Alt görev satırı, üstüne bağlı olduğunu <b>iki kanaldan</b> anlatır:
 * görsel girinti/ok ikonu ve <b>metinsel</b> bir başlık ({@code title} + {@code aria-label}).
 *
 * <p><b>İkon tek başına yetmez</b> (bağımsız code-review bulgusu, 2026-09-16): {@code aria-hidden}
 * bir ok, ekran okuyucuya hiçbir şey söylemez — satırlar düz bir liste olarak okunur ve "bu görev
 * hangi ana göreve bağlı?" bilgisi <b>yalnız gören kullanıcıya</b> ulaşırdı.
 *
 * <p><b>Başlık gerçek bir {@code <button>}'dır</b> (02.10.2026): satırın tamamı fareyle tıklanınca
 * detay pop-up'ını açar, ama tablo satırı klavyeyle odaklanamaz — düğme klavye ve ekran okuyucu
 * yoludur. Düğmeye tıklama PrimeReact'te satır tıklaması sayılmaz, pop-up bir kez açılır. Alt görev
 * ilişkisi artık düğmenin erişilebilir adında taşınır (iç {@code span}'deki etiket, düğmenin adıyla
 * ezileceği için oradan kaldırıldı; görsel ipucu {@code title} yerinde kalır).
 */
function nameBody(
  row: MeetingManagementTask,
  rows: MeetingManagementTask[],
  t: (key: string, options?: Record<string, unknown>) => string,
  onOpenDetail: (row: MeetingManagementTask) => void,
) {
  const parentName = row.subTask ? rows.find((r) => r.id === row.parentId)?.name : undefined
  const relation = parentName
    ? t('meetingManagement.row.subTaskOf', { name: parentName })
    : undefined
  return (
    <button
      type="button"
      className="act-cell-link"
      onClick={() => onOpenDetail(row)}
      aria-label={t('meetingManagement.row.openDetailNamed', {
        name: relation ? `${row.name} — ${relation}` : row.name,
      })}
    >
      {row.subTask ? (
        <span className="mm-subtask-name" title={relation}>
          <i className="pi pi-angle-right" aria-hidden="true" />
          {row.name}
        </span>
      ) : (
        row.name
      )}
    </button>
  )
}
